import { ChallengeCell } from './ChallengeCell'
import type { CalendarChallenge, CalendarCell, EmptyDayCell } from '@/pages/GameArchive'

function isEmptyDayCell(cell: CalendarCell): cell is EmptyDayCell {
  return cell !== null && 'type' in cell && cell.type === 'empty'
}

function isCalendarChallenge(cell: CalendarCell): cell is CalendarChallenge {
  return cell !== null && !('type' in cell) && 'id' in cell
}

interface ArchiveCalendarProps {
  calendarDays: CalendarCell[]
  isTransitioning: boolean
  handleChallengeClick: (challenge: CalendarChallenge) => void
  signatureColor: string
  challengeType?: 'internal' | 'auto-challenge' | 'external'
  numberOfStars: number
}

export function ArchiveCalendar({
  calendarDays,
  isTransitioning,
  handleChallengeClick,
  signatureColor,
  challengeType,
  numberOfStars
}: ArchiveCalendarProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-sm:p-4 shadow-sm">
      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-3 max-sm:gap-2 mb-5 max-sm:mb-3 pb-4 max-sm:pb-3 border-b-2 border-gray-100 dark:border-gray-700">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-center text-sm font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide"
               style={{ fontFamily: 'Oswald, sans-serif' }}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className={`grid grid-cols-7 gap-3 max-sm:gap-2 transition-opacity duration-150 ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}
           data-testid="calendar-grid">
        {calendarDays.map((item, index) => {
          // Offset cell — invisible spacer before the 1st of the month
          if (item === null) {
            return <div key={`empty-${index}`} className="aspect-square" data-testid={`empty-cell-${index}`} />
          }

          // Empty day cell — day of month with no challenge
          if (isEmptyDayCell(item)) {
            return (
              <div
                key={`day-${item.dayNumber}`}
                className="aspect-square flex flex-col items-center justify-center rounded-xl border-2 border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-400 dark:text-gray-500"
                data-testid={`empty-day-${item.dayNumber}`}
              >
                <div className="text-2xl font-bold tracking-wide"
                     style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {item.dayNumber}
                </div>
              </div>
            )
          }

          // Challenge cell
          if (isCalendarChallenge(item)) {
            return (
              <ChallengeCell
                key={item.id}
                challenge={item}
                onClick={handleChallengeClick}
                isNavigating={isTransitioning}
                signatureColor={signatureColor}
                challengeType={challengeType}
                numberOfStars={numberOfStars}
              />
            )
          }

          return null
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-8 max-sm:gap-4 mt-8 max-sm:mt-4 pt-6 max-sm:pt-4 border-t-2 border-gray-100 dark:border-gray-700 justify-center">
        {challengeType === 'auto-challenge' ? (
          // Legend for auto-challenge games (only today is clickable)
          <>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md border-2" style={{ backgroundColor: signatureColor, borderColor: signatureColor }} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}>
                Today (Clickable)
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-100 dark:border-blue-800" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}>
                Past Challenges
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-100 dark:border-gray-700" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}>
                No Challenge
              </span>
            </div>
          </>
        ) : (
          // Legend for regular games (completed and available are clickable)
          <>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md border-2" style={{ backgroundColor: signatureColor, borderColor: signatureColor }} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}>
                Completed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}>
                Available
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-100 dark:border-gray-700" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide"
                    style={{ fontFamily: 'Oswald, sans-serif' }}>
                No Challenge
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
